import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { compressImageToDataURL } from "@/lib/image";
import { toast } from "sonner";
import { setBranding } from "@/lib/branding";
import { Upload, X, ImagePlus, Facebook, Instagram, Youtube } from "lucide-react";

const MAX_LOGO_BYTES = 3 * 1024 * 1024; // 3MB — logos stored inline as data URL

export default function AdminLanding() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const logoFileRef = useRef(null);
  const heroFileRef = useRef(null);
  useEffect(() => { api.get("/landing").then(({ data }) => setForm(data)); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  /* ── Hero image upload ───────────────────────────────────────── */
  const onHeroFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Hero image must be 15 MB or smaller"); return; }
    try {
      // Hero renders full-width — 1920px is a good balance of quality vs size
      const dataUrl = await compressImageToDataURL(f, { maxDim: 1920 });
      set("hero_image_url", dataUrl);
      toast.success("Hero image loaded — press Save to apply");
    } catch {
      toast.error("Could not read the file");
    }
  };
  const clearHero = async () => {
    try {
      await api.delete("/landing/image/hero_image_url");
      set("hero_image_url", "");
      toast.success("Hero image removed");
    } catch {
      toast.error("Failed to remove hero image");
    }
  };

  /* ── Club logo upload ────────────────────────────────────────── */
  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Logo must be 10 MB or smaller before compression"); return; }
    try {
      // Club logo renders at ~48px in the sidebar / public header —
      // 384px is plenty for retina scaling. Transparent PNGs get ivory
      // fill via the shared helper.
      const dataUrl = await compressImageToDataURL(f, { maxDim: 384 });
      set("club_logo_url", dataUrl);
    } catch {
      toast.error("Could not read the file");
    }
  };
  const clearLogo = () => { set("club_logo_url", ""); if (logoFileRef.current) logoFileRef.current.value = ""; };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/landing", {
        club_name: form.club_name, tagline: form.tagline, about: form.about,
        hero_image_url: form.hero_image_url, club_logo_url: form.club_logo_url || "",
        contact_email: form.contact_email,
        contact_phone: form.contact_phone, location: form.location,
        facebook_url: form.facebook_url || "",
        instagram_url: form.instagram_url || "",
        youtube_url: form.youtube_url || "",
      });
      setForm(data);
      // Broadcast so every mounted Brand refreshes (sidebar, public header, etc.)
      setBranding(data);
      toast.success("Landing page updated");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  if (!form) return <AppShell variant="admin" title="Landing page"><div className="text-muted-foreground">Loading…</div></AppShell>;

  return (
    <AppShell variant="admin" title="Landing page">
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-7 rounded-2xl lg:col-span-2 space-y-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Content</div>
          <div className="grid gap-5">
            <div><Label className="text-sm font-medium">Club name</Label><Input value={form.club_name || ""} onChange={(e) => set("club_name", e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="landing-club-name" /></div>
            <div><Label className="text-sm font-medium">Tagline</Label><Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="landing-tagline" /></div>

            {/* Club logo uploader — replaces the "DC" fallback tile in the sidebar / public brand mark */}
            <div>
              <Label className="text-sm font-medium">Club logo</Label>
              <div className="text-[11px] text-muted-foreground mt-0.5 mb-2">Shown in the sidebar and public site header. PNG / JPG / SVG · auto-shrunk to 384px. Square works best.</div>
              <div className="mt-1.5 flex items-center gap-4 flex-wrap">
                <div
                  className="h-20 w-20 rounded-xl border border-dashed border-border bg-[hsl(var(--secondary))]/40 flex items-center justify-center overflow-hidden shrink-0"
                  data-testid="landing-logo-preview"
                >
                  {form.club_logo_url ? (
                    <img src={form.club_logo_url} alt="Club logo preview" className="h-full w-full object-contain" data-testid="landing-logo-preview-img" />
                  ) : (
                    <ImagePlus className="h-6 w-6 text-muted-foreground/60" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => logoFileRef.current?.click()} data-testid="landing-logo-upload">
                      <Upload className="h-3.5 w-3.5 mr-1.5" /> {form.club_logo_url ? "Replace logo" : "Upload logo"}
                    </Button>
                    {form.club_logo_url && (
                      <Button type="button" variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={clearLogo} data-testid="landing-logo-clear">
                        <X className="h-3.5 w-3.5 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  <input ref={logoFileRef} type="file" accept="image/png,.png" className="hidden" onChange={onLogoFile} data-testid="landing-logo-file" />
                </div>
              </div>
            </div>

            <div><Label className="text-sm font-medium">About</Label><Textarea rows={5} value={form.about || ""} onChange={(e) => set("about", e.target.value)} className="mt-1.5 rounded-lg" data-testid="landing-about" /></div>

            {/* Hero image uploader — the full-width background behind the landing CTA */}
            <div>
              <Label className="text-sm font-medium">Hero image</Label>
              <div className="text-[11px] text-muted-foreground mt-0.5 mb-2">Full-width background image for the landing page hero. Landscape photos work best. Auto-compressed to 1920 px.</div>
              <div className="mt-1.5 flex items-center gap-4 flex-wrap">
                <div
                  className="h-20 w-36 rounded-xl border border-dashed border-border bg-[hsl(var(--secondary))]/40 flex items-center justify-center overflow-hidden shrink-0"
                  data-testid="landing-hero-preview"
                >
                  {form.hero_image_url ? (
                    <img src={form.hero_image_url} alt="Hero preview" className="h-full w-full object-cover" data-testid="landing-hero-preview-img" />
                  ) : (
                    <ImagePlus className="h-6 w-6 text-muted-foreground/60" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => heroFileRef.current?.click()} data-testid="landing-hero-upload">
                      <Upload className="h-3.5 w-3.5 mr-1.5" /> {form.hero_image_url ? "Replace hero" : "Upload hero image"}
                    </Button>
                    {form.hero_image_url && (
                      <Button type="button" variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={clearHero} data-testid="landing-hero-clear">
                        <X className="h-3.5 w-3.5 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  <input ref={heroFileRef} type="file" accept="image/png,.png" className="hidden" onChange={onHeroFile} data-testid="landing-hero-file" />
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm font-medium">Contact email</Label><Input value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>
              <div><Label className="text-sm font-medium">Contact phone</Label><Input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>
              <div className="sm:col-span-2"><Label className="text-sm font-medium">Location</Label><Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>
            </div>
          </div>

          {/* Social links — surfaced as pill icons on the public landing CTA.
              Leave a field blank to hide that icon. */}
          <div className="pt-2">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Social links</div>
            <div className="text-[11px] text-muted-foreground -mt-2 mb-3">
              Displayed as icons under the CTA on the public site. Leave blank to hide.
            </div>
            <div className="grid gap-4">
              <div>
                <Label className="text-sm font-medium flex items-center gap-2"><Facebook className="h-4 w-4" /> Facebook URL</Label>
                <Input
                  type="url"
                  value={form.facebook_url || ""}
                  onChange={(e) => set("facebook_url", e.target.value)}
                  placeholder="https://www.facebook.com/yourclub"
                  className="mt-1.5 h-11 rounded-lg"
                  data-testid="landing-facebook-url"
                />
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-2"><Instagram className="h-4 w-4" /> Instagram URL</Label>
                <Input
                  type="url"
                  value={form.instagram_url || ""}
                  onChange={(e) => set("instagram_url", e.target.value)}
                  placeholder="https://www.instagram.com/yourclub"
                  className="mt-1.5 h-11 rounded-lg"
                  data-testid="landing-instagram-url"
                />
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-2"><Youtube className="h-4 w-4" /> YouTube URL</Label>
                <Input
                  type="url"
                  value={form.youtube_url || ""}
                  onChange={(e) => set("youtube_url", e.target.value)}
                  placeholder="https://www.youtube.com/@yourclub"
                  className="mt-1.5 h-11 rounded-lg"
                  data-testid="landing-youtube-url"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button disabled={busy} onClick={save} className="h-11 px-6 btn-primary-modern" data-testid="landing-save">{busy ? "Saving…" : "Save changes"}</Button>
          </div>
        </Card>
        <Card className="p-5 rounded-2xl h-fit sticky top-24">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3">Preview</div>
          <div className="aspect-video rounded-xl overflow-hidden border border-border">
            {form.hero_image_url ? <img src={form.hero_image_url} alt="" className="w-full h-full object-cover" /> : <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No hero image</div>}
          </div>
          <div className="mt-4 flex items-center gap-3">
            {form.club_logo_url ? (
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-md overflow-hidden border border-border bg-[hsl(var(--secondary))]/40 shrink-0">
                <img src={form.club_logo_url} alt="" className="h-full w-full object-contain" />
              </span>
            ) : null}
            <div className="min-w-0">
              <div className="font-bold text-lg tracking-tight truncate">{form.club_name}</div>
              <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{form.tagline}</div>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
